import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  IconButton,
  Tooltip,
  Checkbox,
  Divider,
  Button,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from '@chakra-ui/icons';
import {
  RAG_COLORS,
  formatCurrency,
  formatRatio,
  type RAGStatus,
} from 'utils/rag';
import { downloadCsvFile, toFileSlug, todayStr } from 'utils/csv';

// ── Types ────────────────────────────────────────────────────────────────────

export interface BrokerRow {
  label: string;
  email: string;
  branch_id: number | null;
  score: number;
  score_r30?: number;
  quotes: number;
  quotes_per_week?: number;
  closing_ratio: number;
  nb_premium_total: number;
  nb_premium_pl?: number;
  nb_premium_cl?: number;
  nb_premium_lf?: number;
  p2p_percent: number;
  p2p_goal?: number;
  p2p_pace?: number;
  archived: number;
  leads_call_list: number;
  leads_organic: number;
  leads_direct: number;
  tbv?: number;
  tbv_pl?: number;
  tbv_cl?: number;
  tbv_lf?: number;
  broker_productivity?: number;
  broker_score?: number;
  premium_score?: number;
  quote_volume_score?: number;
  closing_ratio_score?: number;
  performance_score?: number;
  retention?: number;
  loss_ratio?: number;
  multiline_pct?: number;
  broker_count_l60?: number;
  daily_call_avg_monthly?: number;
  avg_leads_per_day_r30?: number;
  total_premium?: number;
  actual_pct_pl?: number;
  actual_pct_cl?: number;
  actual_pct_lf?: number;
  lead_source_breakdown?: Record<
    string,
    {
      leads: number;
      won: number;
      lost: number;
      archived: number;
      nb: number;
      close_rate: number;
    }
  >;
  rag_score: RAGStatus;
  rag_quotes: RAGStatus;
  rag_closing: RAGStatus;
  rag_p2p: RAGStatus;
}

export interface BranchRow extends BrokerRow {
  children: BrokerRow[];
}

type SortKey =
  | 'score'
  | 'quotes'
  | 'closing_ratio'
  | 'nb_premium_total'
  | 'p2p_percent';

const SORT_LABELS: Record<SortKey, string> = {
  score: 'Score',
  nb_premium_total: 'NB Premium',
  closing_ratio: 'Closing %',
  p2p_percent: 'P2P %',
  quotes: 'Quotes',
};

interface Props {
  data: BranchRow[];
  isLoading?: boolean;
  viewLabel?: string;
  aggregateMode?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  onBranchClick?: (branchId: number) => void;
  onBrokerClick?: (email: string) => void;
  onBranchHover?: (branchId: number) => void;
  onBrokerHover?: (email: string) => void;
  activeBranchId?: number | null;
  selectedBranchId?: number | null;
  sortKey?: SortKey;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: SortKey, dir: 'asc' | 'desc') => void;
  month?: string;
  /** When true, suppresses the broker accordion inside the active branch — brokers
   *  are shown in the DetailPanel above, so showing them here would duplicate. */
  hideExpandedContent?: boolean;
}

// ── Shared helpers ───────────────────────────────────────────────────────────

function downloadCSV(data: BranchRow[], label: string, month: string) {
  const headers = [
    'Name',
    'Score',
    'Quotes',
    'Closing Ratio',
    'NB Premium',
    'P2P %',
    'Archived',
    'Call List',
    'Organic',
    'Direct',
  ];
  const rows: string[][] = [];
  data.forEach((branch) => {
    rows.push([
      branch.label,
      String(Math.round(branch.score)),
      String(branch.quotes),
      formatRatio(branch.closing_ratio),
      formatCurrency(branch.nb_premium_total),
      `${Math.round(branch.p2p_percent)}%`,
      String(branch.archived),
      String(branch.leads_call_list),
      String(branch.leads_organic),
      String(branch.leads_direct),
    ]);
    branch.children.forEach((broker) => {
      rows.push([
        `  ${broker.label}`,
        String(Math.round(broker.score)),
        String(broker.quotes),
        formatRatio(broker.closing_ratio),
        formatCurrency(broker.nb_premium_total),
        `${Math.round(broker.p2p_percent)}%`,
        String(broker.archived),
        String(broker.leads_call_list),
        String(broker.leads_organic),
        String(broker.leads_direct),
      ]);
    });
  });
  const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
  downloadCsvFile(csv, `analytics_${toFileSlug(label)}_${month}_${todayStr()}`);
}

// ── Accordion mode: expanded branch content ──────────────────────────────────

function BranchExpandedContent({
  branch,
  onBrokerClick,
  aggregateMode,
  selectedIds,
  onSelectionChange,
}: {
  branch: BranchRow;
  onBrokerClick?: (email: string) => void;
  aggregateMode?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
}) {
  const sortedBrokers = useMemo(
    () => [...branch.children].sort((a, b) => b.score - a.score),
    [branch.children],
  );

  const toggleBroker = (email: string) => {
    if (!onSelectionChange || !selectedIds) return;
    const key = `broker:${email}`;
    const next = new Set(selectedIds);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  };

  return (
    <Box p="16px" borderTop="1px solid" borderColor="gray.100">
      {sortedBrokers.length > 0 && (
        <>
          <Divider mb="12px" />
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            letterSpacing="wider"
            mb="8px"
          >
            BROKERS
          </Text>
          <Box
            overflowX="auto"
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.100"
          >
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  {aggregateMode && <Th w="36px" />}
                  <Th minW="140px">Broker</Th>
                  <Th textAlign="center">Score</Th>
                  <Th textAlign="center">Closing %</Th>
                  <Th textAlign="center">NB Premium</Th>
                  <Th textAlign="center">P2P %</Th>
                  <Th textAlign="center">Archived</Th>
                  <Th textAlign="center">Leads</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedBrokers.map((b) => {
                  const sc = RAG_COLORS[b.rag_score];
                  const brokerKey = `broker:${b.email}`;
                  const isSelected = selectedIds?.has(brokerKey) ?? false;
                  return (
                    <Tr
                      key={b.email}
                      bg={isSelected ? 'brand.50' : undefined}
                      _hover={{
                        bg: isSelected
                          ? 'brand.100'
                          : onBrokerClick && !aggregateMode
                            ? 'brand.50'
                            : 'gray.50',
                      }}
                      cursor={
                        onBrokerClick && !aggregateMode ? 'pointer' : undefined
                      }
                      onClick={
                        onBrokerClick && !aggregateMode
                          ? () => onBrokerClick(b.email)
                          : undefined
                      }
                    >
                      {aggregateMode && (
                        <Td w="36px" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            isChecked={isSelected}
                            onChange={() => toggleBroker(b.email)}
                            colorScheme="brand"
                            size="sm"
                          />
                        </Td>
                      )}
                      <Td>
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color={
                            onBrokerClick && !aggregateMode
                              ? 'brand.600'
                              : 'gray.700'
                          }
                        >
                          {b.label}
                        </Text>
                        <Text fontSize="10px" color="gray.400">
                          {b.email}
                        </Text>
                      </Td>
                      <Td textAlign="center">
                        <Box
                          display="inline-block"
                          minW="32px"
                          px="6px"
                          py="1px"
                          borderRadius="5px"
                          bg={sc?.bg}
                          color={sc?.text}
                          fontWeight="600"
                          fontSize="xs"
                          textAlign="center"
                        >
                          {Math.round(b.score)}
                        </Box>
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {formatRatio(b.closing_ratio)}
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {formatCurrency(b.nb_premium_total)}
                      </Td>
                      <Td textAlign="center" fontSize="xs">
                        {Math.round(b.p2p_percent)}%
                      </Td>
                      <Td textAlign="center" fontSize="xs" color="red.400">
                        {b.archived}
                      </Td>
                      <Td>
                        <Flex gap="3px" justify="center">
                          <Tooltip label="Call List">
                            <Box
                              display="inline-block"
                              minW="28px"
                              px="5px"
                              py="1px"
                              borderRadius="5px"
                              bg="blue.50"
                              color="blue.700"
                              fontWeight="600"
                              fontSize="xs"
                              textAlign="center"
                            >
                              {b.leads_call_list}
                            </Box>
                          </Tooltip>
                          <Tooltip label="Organic">
                            <Box
                              display="inline-block"
                              minW="28px"
                              px="5px"
                              py="1px"
                              borderRadius="5px"
                              bg="green.50"
                              color="green.700"
                              fontWeight="600"
                              fontSize="xs"
                              textAlign="center"
                            >
                              {b.leads_organic}
                            </Box>
                          </Tooltip>
                          <Tooltip label="Direct">
                            <Box
                              display="inline-block"
                              minW="28px"
                              px="5px"
                              py="1px"
                              borderRadius="5px"
                              bg="orange.50"
                              color="orange.700"
                              fontWeight="600"
                              fontSize="xs"
                              textAlign="center"
                            >
                              {b.leads_direct}
                            </Box>
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Accordion mode: branch card ───────────────────────────────────────────────

function AccordionBranchCard({
  row,
  isExpanded,
  isFiltered,
  onToggle,
  onBrokerClick,
  onBranchClick,
  aggregateMode,
  selectedIds,
  onSelectionChange,
  hideExpandedContent,
}: {
  row: BranchRow;
  isExpanded: boolean;
  isFiltered: boolean;
  onToggle: () => void;
  onBrokerClick?: (email: string) => void;
  onBranchClick?: (id: number) => void;
  aggregateMode?: boolean;
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  hideExpandedContent?: boolean;
}) {
  const sc = RAG_COLORS[row.rag_score];
  const p2pColor =
    row.p2p_percent >= 100
      ? 'green.600'
      : row.p2p_percent >= 75
        ? 'orange.500'
        : 'red.500';

  const allBrokerKeys = row.children.map((b) => `broker:${b.email}`);
  const selectedBrokerCount = allBrokerKeys.filter((k) =>
    selectedIds?.has(k),
  ).length;
  const isSelected =
    allBrokerKeys.length > 0 && selectedBrokerCount === allBrokerKeys.length;
  const isIndeterminate = selectedBrokerCount > 0 && !isSelected;

  const toggleBranch = () => {
    if (!onSelectionChange || !selectedIds) return;
    const next = new Set(selectedIds);
    if (isSelected || isIndeterminate) {
      allBrokerKeys.forEach((k) => next.delete(k));
    } else {
      allBrokerKeys.forEach((k) => next.add(k));
    }
    onSelectionChange(next);
  };

  const handleClick = () => {
    if (!isFiltered && onBranchClick && row.branch_id != null) {
      onBranchClick(row.branch_id);
      if (isExpanded) return;
    }
    onToggle();
  };

  return (
    <Box
      border="1px solid"
      borderColor={
        isSelected || isIndeterminate
          ? 'brand.400'
          : isExpanded
            ? 'brand.200'
            : 'gray.200'
      }
      borderRadius="12px"
      mb="8px"
      overflow="hidden"
    >
      <Flex
        align="center"
        px="16px"
        py="12px"
        cursor="pointer"
        onClick={handleClick}
        bg={
          isSelected || isIndeterminate
            ? 'brand.50'
            : isExpanded
              ? 'brand.50'
              : 'white'
        }
        _hover={{
          bg:
            isSelected || isIndeterminate
              ? 'brand.100'
              : isExpanded
                ? 'brand.100'
                : 'gray.50',
        }}
        gap="12px"
        wrap="wrap"
      >
        {aggregateMode && (
          <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              isChecked={isSelected}
              isIndeterminate={isIndeterminate}
              onChange={toggleBranch}
              colorScheme="brand"
              size="md"
            />
          </Box>
        )}

        <IconButton
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          size="xs"
          variant="ghost"
          colorScheme="brand"
          flexShrink={0}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        />

        <Text
          fontWeight="700"
          fontSize="sm"
          color="gray.800"
          flex="1"
          minW="120px"
        >
          {row.label}
        </Text>

        {/* Score badge */}
        <Box
          display="inline-block"
          px="10px"
          py="3px"
          borderRadius="6px"
          bg={sc?.bg}
          color={sc?.text}
          fontWeight="700"
          fontSize="sm"
          minW="40px"
          textAlign="center"
          flexShrink={0}
        >
          {Math.round(row.score)}
        </Box>

        {/* Metrics strip */}
        <Flex align="center" flexShrink={0}>
          <Box
            w="110px"
            textAlign="right"
            borderRight="1px solid"
            borderColor="gray.100"
            pr="16px"
            mr="16px"
          >
            <Text fontSize="10px" color="gray.600" lineHeight="1.2">
              NB Premium
            </Text>
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              {formatCurrency(row.nb_premium_total)}
            </Text>
          </Box>
          <Box
            w="56px"
            textAlign="right"
            borderRight="1px solid"
            borderColor="gray.100"
            pr="16px"
            mr="16px"
          >
            <Text fontSize="10px" color="gray.600" lineHeight="1.2">
              P2P
            </Text>
            <Text fontSize="sm" fontWeight="600" color={p2pColor}>
              {Math.round(row.p2p_percent)}%
            </Text>
          </Box>
          <Box
            w="56px"
            textAlign="right"
            borderRight="1px solid"
            borderColor="gray.100"
            pr="16px"
            mr="16px"
          >
            <Text fontSize="10px" color="gray.600" lineHeight="1.2">
              Closing
            </Text>
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              {formatRatio(row.closing_ratio)}
            </Text>
          </Box>
          <Box
            w="52px"
            textAlign="right"
            borderRight="1px solid"
            borderColor="gray.100"
            pr="16px"
            mr="16px"
          >
            <Text fontSize="10px" color="gray.600" lineHeight="1.2">
              Quotes
            </Text>
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              {row.quotes}
            </Text>
          </Box>
          <Box w="56px" textAlign="right">
            <Text fontSize="10px" color="gray.600" lineHeight="1.2">
              Archived
            </Text>
            <Text
              fontSize="sm"
              fontWeight="600"
              color={row.archived > 0 ? 'red.400' : 'gray.700'}
            >
              {row.archived}
            </Text>
          </Box>
        </Flex>
      </Flex>

      {isExpanded && !hideExpandedContent && (
        <BranchExpandedContent
          branch={row}
          onBrokerClick={!aggregateMode ? onBrokerClick : undefined}
          aggregateMode={aggregateMode}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
        />
      )}
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export const BranchBrokerTable = ({
  data,
  isLoading,
  viewLabel,
  aggregateMode,
  selectedIds,
  onSelectionChange,
  onBranchClick,
  onBrokerClick,
  onBranchHover,
  onBrokerHover,
  activeBranchId,
  selectedBranchId,
  sortKey: controlledSortKey,
  sortDir: controlledSortDir,
  onSortChange,
  month,
  hideExpandedContent,
}: Props) => {
  const [internalSortKey, setInternalSortKey] = useState<SortKey>('score');
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>(
    'desc',
  );
  const [expandedBranchId, setExpandedBranchId] = useState<number | null>(
    activeBranchId ?? null,
  );

  useEffect(() => {
    if (activeBranchId != null) setExpandedBranchId(activeBranchId);
  }, [activeBranchId]);

  const handleToggleBranch = (branchId: number | null) => {
    setExpandedBranchId((prev) => (prev === branchId ? null : branchId));
  };

  const sortKey = controlledSortKey ?? internalSortKey;
  const sortDir = controlledSortDir ?? internalSortDir;

  const handleSort = (col: SortKey) => {
    const newDir =
      col === sortKey ? (sortDir === 'desc' ? 'asc' : 'desc') : 'desc';
    if (onSortChange) onSortChange(col, newDir);
    else {
      setInternalSortKey(col);
      setInternalSortDir(newDir);
    }
  };

  const sorted = useMemo(
    () =>
      [...data].sort((a, b) => {
        const diff = a[sortKey] - b[sortKey];
        return sortDir === 'desc' ? -diff : diff;
      }),
    [data, sortKey, sortDir],
  );

  const effectiveMonth =
    month ||
    (() => {
      const n = new Date();
      return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
    })();

  const selectedCount = selectedIds?.size ?? 0;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="12px">
        <Flex align="center" gap="10px">
          <Text fontWeight="700" fontSize="md" color="gray.800">
            Branches & Broker Performance
          </Text>
          {aggregateMode && selectedCount > 0 && (
            <Text fontSize="xs" color="brand.600" fontWeight="600">
              {selectedCount} selected
            </Text>
          )}
        </Flex>
        <Tooltip
          label={
            isLoading
              ? 'Loading…'
              : data.length === 0
                ? 'No data'
                : 'Download CSV'
          }
        >
          <IconButton
            aria-label="Download"
            icon={<DownloadIcon />}
            size="sm"
            variant="outline"
            isDisabled={isLoading || data.length === 0}
            onClick={() =>
              downloadCSV(data, viewLabel ?? 'BIG', effectiveMonth)
            }
          />
        </Tooltip>
      </Flex>

      {/* Sort pills — visible in both modes */}
      <Flex gap="6px" mb="12px" align="center" flexWrap="wrap">
        <Text fontSize="xs" color="gray.500" mr="2px">
          Sort by:
        </Text>
        {(
          [
            'score',
            'nb_premium_total',
            'closing_ratio',
            'p2p_percent',
            'quotes',
          ] as SortKey[]
        ).map((key) => (
          <Button
            key={key}
            size="xs"
            variant={sortKey === key ? 'solid' : 'outline'}
            colorScheme={sortKey === key ? 'brand' : 'gray'}
            onClick={() => handleSort(key)}
            fontSize="xs"
            rightIcon={
              sortKey === key ? (
                sortDir === 'desc' ? (
                  <TriangleDownIcon boxSize="8px" />
                ) : (
                  <TriangleUpIcon boxSize="8px" />
                )
              ) : undefined
            }
          >
            {SORT_LABELS[key]}
          </Button>
        ))}
      </Flex>

      {/* Accordion cards — used in both Overview and Aggregate modes */}
      {sorted.length === 0 ? (
        <Box
          textAlign="center"
          color="gray.400"
          py="40px"
          borderRadius="12px"
          border="1px dashed"
          borderColor="gray.200"
        >
          No data available
        </Box>
      ) : (
        sorted.map((branch) => (
          <AccordionBranchCard
            key={branch.branch_id ?? branch.label}
            row={branch}
            isExpanded={expandedBranchId === branch.branch_id}
            isFiltered={selectedBranchId === branch.branch_id}
            onToggle={() => handleToggleBranch(branch.branch_id)}
            onBrokerClick={onBrokerClick}
            onBranchClick={onBranchClick}
            aggregateMode={aggregateMode}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
            hideExpandedContent={
              hideExpandedContent && expandedBranchId === branch.branch_id
            }
          />
        ))
      )}
    </Box>
  );
};

// ── Aggregate broker table ────────────────────────────────────────────────────

export type AggregateBrokerRow = BrokerRow & { branch_label: string };

type AggSortKey =
  | 'score'
  | 'quotes'
  | 'closing_ratio'
  | 'nb_premium_total'
  | 'p2p_percent'
  | 'archived';

const AGG_SORT_LABELS: Record<AggSortKey, string> = {
  score: 'Score',
  quotes: 'Quotes',
  closing_ratio: 'Closing %',
  nb_premium_total: 'NB Premium',
  p2p_percent: 'P2P %',
  archived: 'Archived',
};

export function AggregateBrokerTable({
  rows,
  month,
  onBrokerClick,
}: {
  rows: AggregateBrokerRow[];
  month: string;
  onBrokerClick?: (email: string) => void;
}) {
  const [sortKey, setSortKey] = useState<AggSortKey>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: AggSortKey) => {
    if (col === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(col);
      setSortDir('desc');
    }
  };

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const diff = (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
        return sortDir === 'desc' ? -diff : diff;
      }),
    [rows, sortKey, sortDir],
  );

  if (rows.length === 0) return null;

  const [yr, mo] = month.split('-');
  const monthLabel = new Date(Number(yr), Number(mo) - 1, 1).toLocaleString(
    'en-CA',
    { month: 'long', year: 'numeric' },
  );

  return (
    <Box>
      <Text
        fontSize="xs"
        fontWeight="700"
        color="gray.500"
        letterSpacing="wider"
        mb="10px"
      >
        AGGREGATED BROKERS — {monthLabel.toUpperCase()}
      </Text>
      <Box
        overflowX="auto"
        borderRadius="8px"
        border="1px solid"
        borderColor="gray.200"
      >
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th minW="150px">Broker</Th>
              <Th minW="130px">Branch</Th>
              {(Object.keys(AGG_SORT_LABELS) as AggSortKey[]).map((col) => (
                <Th
                  key={col}
                  textAlign="center"
                  cursor="pointer"
                  userSelect="none"
                  color={sortKey === col ? 'brand.600' : undefined}
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => handleSort(col)}
                >
                  <Flex align="center" justify="center" gap="3px">
                    {AGG_SORT_LABELS[col]}
                    {sortKey === col && (
                      <Text as="span" fontSize="9px">
                        {sortDir === 'desc' ? '▼' : '▲'}
                      </Text>
                    )}
                  </Flex>
                </Th>
              ))}
              <Th textAlign="center">Leads</Th>
            </Tr>
          </Thead>
          <Tbody>
            {sorted.map((b) => {
              const sc = RAG_COLORS[b.rag_score];
              const clickable = !!onBrokerClick;
              return (
                <Tr
                  key={b.email}
                  cursor={clickable ? 'pointer' : undefined}
                  _hover={{ bg: clickable ? 'brand.50' : 'gray.50' }}
                  onClick={clickable ? () => onBrokerClick(b.email) : undefined}
                >
                  <Td>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color={clickable ? 'brand.600' : 'gray.700'}
                    >
                      {b.label}
                    </Text>
                    <Text fontSize="10px" color="gray.400">
                      {b.email}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.500">
                      {b.branch_label}
                    </Text>
                  </Td>
                  <Td textAlign="center">
                    <Box
                      display="inline-block"
                      minW="32px"
                      px="6px"
                      py="1px"
                      borderRadius="5px"
                      bg={sc?.bg}
                      color={sc?.text}
                      fontWeight="600"
                      fontSize="xs"
                      textAlign="center"
                    >
                      {Math.round(b.score)}
                    </Box>
                  </Td>
                  <Td textAlign="center" fontSize="xs">
                    {b.quotes}
                  </Td>
                  <Td textAlign="center" fontSize="xs">
                    {formatRatio(b.closing_ratio)}
                  </Td>
                  <Td textAlign="center" fontSize="xs">
                    {formatCurrency(b.nb_premium_total)}
                  </Td>
                  <Td
                    textAlign="center"
                    fontSize="xs"
                    color={
                      b.p2p_percent >= 100
                        ? 'green.600'
                        : b.p2p_percent >= 75
                          ? 'orange.500'
                          : 'red.500'
                    }
                    fontWeight="600"
                  >
                    {Math.round(b.p2p_percent)}%
                  </Td>
                  <Td
                    textAlign="center"
                    fontSize="xs"
                    color={b.archived > 0 ? 'red.400' : 'gray.400'}
                  >
                    {b.archived}
                  </Td>
                  <Td>
                    <Flex gap="3px" justify="center">
                      <Tooltip label="Call List">
                        <Box
                          display="inline-block"
                          minW="28px"
                          px="5px"
                          py="1px"
                          borderRadius="5px"
                          bg="blue.50"
                          color="blue.700"
                          fontWeight="600"
                          fontSize="xs"
                          textAlign="center"
                        >
                          {b.leads_call_list}
                        </Box>
                      </Tooltip>
                      <Tooltip label="Organic">
                        <Box
                          display="inline-block"
                          minW="28px"
                          px="5px"
                          py="1px"
                          borderRadius="5px"
                          bg="green.50"
                          color="green.700"
                          fontWeight="600"
                          fontSize="xs"
                          textAlign="center"
                        >
                          {b.leads_organic}
                        </Box>
                      </Tooltip>
                      <Tooltip label="Direct">
                        <Box
                          display="inline-block"
                          minW="28px"
                          px="5px"
                          py="1px"
                          borderRadius="5px"
                          bg="orange.50"
                          color="orange.700"
                          fontWeight="600"
                          fontSize="xs"
                          textAlign="center"
                        >
                          {b.leads_direct}
                        </Box>
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}
